/**
 * Shared discount-code copy control — <discount-code-copy>
 *
 * Attributes:
 *   data-code     Text to copy (required)
 *   data-success  "true" | "false" — toggled after copy for icon swap
 *
 * Click is handled on the element; stops propagation so Swiper / parent handlers
 * do not intercept. Clipboard API with textarea fallback for non-secure contexts.
 */
if (!customElements.get('discount-code-copy')) {
  class DiscountCodeCopy extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      if (!this.hasAttribute('data-success')) this.setAttribute('data-success', 'false');

      this._onActivate = this._onActivate.bind(this);
      this.addEventListener('click', this._onActivate);
      this.addEventListener('keydown', this._onActivate);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onActivate);
      this.removeEventListener('keydown', this._onActivate);
      if (this._resetTimer) window.clearTimeout(this._resetTimer);
    }

    _onActivate(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();

      const code = (this.getAttribute('data-code') || '').trim();
      if (!code) return;

      this._copy(code)
        .then(() => {
          this.setAttribute('data-success', 'true');
          if (this._resetTimer) window.clearTimeout(this._resetTimer);
          this._resetTimer = window.setTimeout(() => {
            this.setAttribute('data-success', 'false');
          }, 2000);
        })
        .catch(() => {
          /* ignore */
        });
    }

    _copy(text) {
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text).catch(() => this._copyFallback(text));
      }
      return this._copyFallback(text);
    }

    _copyFallback(text) {
      return new Promise((resolve, reject) => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.setAttribute('aria-hidden', 'true');
          ta.style.position = 'fixed';
          ta.style.top = '0';
          ta.style.left = '0';
          ta.style.width = '1px';
          ta.style.height = '1px';
          ta.style.padding = '0';
          ta.style.border = 'none';
          ta.style.outline = 'none';
          ta.style.boxShadow = 'none';
          ta.style.background = 'transparent';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          ta.setSelectionRange(0, ta.value.length);
          const ok = document.execCommand('copy');
          document.body.removeChild(ta);
          if (ok) resolve();
          else reject(new Error('copy failed'));
        } catch (e) {
          reject(e);
        }
      });
    }
  }

  customElements.define('discount-code-copy', DiscountCodeCopy);
}
